// pages/compliance/ComplianceChecklistView.tsx
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Flex,
  List,
  Spin,
  Input,
  Popover,
  Modal,
  Slider,
  Image,
  message,
} from "antd";
import {
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  FormOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./style.css";
import { toast } from "react-toastify";
import { useSelector } from "react-redux"; // ✅ ADD
import LocationSelector from "../../components/LocationSelector"; // ✅ ADD
import ChecklistTaskNote from "../../components/ChecklistTaskNote";
import {
  useFetchSelfRestaurantDataQuery,
  useUpdateRestaurantSettingMutation,
} from "../restaurant/restaruant.service";
import {
  useCreateComplianceSubmissionMutation,
  useFetchCompliancesWithSubmissionQuery,
} from "./compliance.service";
import { BACKEND_URL } from "../../constant";

const ComplianceChecklistView = () => {
  const { t, i18n } = useTranslation();
  const { type } = useParams();
  const curLanguage = i18n.language;
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user); // ✅ ADD

  // ✅ Location state
  const [locationId, setLocationId] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalId, setIsModalId] = useState();
  const [modalContent, setModalContent] = useState();
  const [contents, setContents] = useState({});
  const [checkTempSlider, setCheckTempSlider] = useState({});
  const [openPopoverId, setOpenPopoverId] = useState(null);

  // ✅ Set initial location from user
  useEffect(() => {
    const locId = user?.activeLocationId || user?.restaurantId;
    if (locId) {
      setLocationId(locId);
    }
  }, [user]);

  // ✅ Handle location change
  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    setContents({});
    setCheckTempSlider({});
  };

  // ✅ Fetch submissions for this location
  const {
    data = { data: { compliances: [] } },
    isFetching,
    isSuccess,
    refetch,
  } = useFetchCompliancesWithSubmissionQuery(
    { type, locationId }, // ✅ Pass locationId
    {
      refetchOnMountOrArgChange: true,
      skip: !locationId,
    }
  );

  const {
    data: { compliances },
  } = data;

  // ✅ Fetch restaurant data for this location
  const {
    data: restaurantData = {
      data: { restaurant: { complianceLevel: [] } },
    },
  } = useFetchSelfRestaurantDataQuery(locationId, {
    refetchOnMountOrArgChange: true,
    skip: !locationId,
  });

  const {
    data: {
      restaurant: { complianceLevel },
    },
  } = restaurantData;

  const [requestCreateChecklistUpdated] =
    useCreateComplianceSubmissionMutation();

  const [
    requestUpdateRestaurantSetting,
    { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess },
  ] = useUpdateRestaurantSettingMutation();

  const handleCompleteChecklist = async (data) => {
    try {
      await requestCreateChecklistUpdated({
        data,
        locationId, // ✅ Pass locationId
      }).unwrap();
      refetch();
      toast.success(t("Checklist updated successfully"));
    } catch (error) {
      console.error("Failed to update checklist:", error);
      toast.error(t("Failed to update checklist"));
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      setIsModalId();
      setModalContent();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isSuccess && compliances.length > 0) {
      setCheckTempSlider(
        compliances.reduce((obj, cur) => {
          obj[cur._id] = cur.submissionData?.temperature || 0;
          return obj;
        }, {})
      );
    }
  }, [compliances, isSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      navigate("/compliance_checks");
    }
  }, [isUpdateLoading, isUpdateSuccess]);

  // ✅ Check if all tasks are completed
  const allCompleted = compliances.filter((item) => !item.isSection).length > 0 &&
    compliances.filter((item) => !item.isSection).length ===
    compliances.filter(
      (item) =>
        !item.isSection &&
        (item.submissionData?.isCompleted ||
          (item.submissionData?.comment?.default &&
            item.submissionData?.comment?.default !== ""))
    ).length;

  // ✅ Check if any task is in progress
  const hasProgress = compliances.some(
    (item) =>
      !item.isSection &&
      !item.submissionData?.isCompleted &&
      !item.submissionData?.comment?.default
  );

  return (
    <Spin spinning={isFetching}>
      <Card
        title={
          <Flex align="center" gap="middle" wrap>
            <span>{t(type || "Compliance Checklist")}</span>
            {/* ✅ LocationSelector */}
            <LocationSelector onLocationChange={handleLocationChange} />
          </Flex>
        }
        className="opencheck-list-page"
        extra={
          <Flex justify="center">
            <Button onClick={() => navigate("/compliance_checks")}>
              {t("Back")}
            </Button>
          </Flex>
        }
        bordered={false}
      >
        {!locationId ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            {t("Please select a location to view compliance checklist")}
          </div>
        ) : (
          <>
            <List
              bordered
              dataSource={compliances}
              renderItem={(item, key) => (
                <List.Item
                  key={key}
                  className={item.isSection && "background-section"}
                >
                  {item.isSection ? (
                    <Row className="opencheck-list-page-list-item">
                      <Col span={24} className="opencheck-list-page-title">
                        <div className="opencheck-list-page-title-description">
                          <div
                            className="compliance-checklist-content"
                            dangerouslySetInnerHTML={{
                              __html:
                                item["title"][curLanguage] ||
                                item["title"]["default"],
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Row className="opencheck-list-page-list-item">
                        <Col span={24} className="opencheck-list-page-title">
                          <div className="opencheck-list-page-title-label">
                            <div
                              className="compliance-checklist-content"
                              dangerouslySetInnerHTML={{
                                __html:
                                  item["title"][curLanguage] ||
                                  item["title"]["default"],
                              }}
                            />
                          </div>
                          <ChecklistTaskNote
                            comment={item.submissionData?.comment}
                            language={curLanguage}
                          />
                          <div className="compliance-list-page-title-description">
                            {item.isInput && (
                              <div style={{ padding: "5px 10px" }}>
                                <Input
                                  defaultValue={
                                    item.submissionData?.content?.[curLanguage] ||
                                    item.submissionData?.content?.default
                                  }
                                  style={{
                                    background: "transparent",
                                    maxWidth: "500px",
                                  }}
                                  onChange={(c) =>
                                    setContents((e) => ({
                                      ...e,
                                      [item._id]: c.target.value,
                                    }))
                                  }
                                />
                              </div>
                            )}
                            {item.isDateRecord && (
                              <div style={{ padding: "0 10px" }}>
                                *RECORD TIME AND DATE AUTOMATICALLY*
                              </div>
                            )}
                            {item.isImage && item.imageUrl && (
                              <img
                                src={`${BACKEND_URL}${item.imageUrl}`}
                                alt="Uploaded compliance image"
                                style={{
                                  objectFit: "cover",
                                  border: "1px solid #d9d9d9",
                                  borderRadius: "6px",
                                  width: "50%",
                                  height: "auto",
                                }}
                              />
                            )}
                            {item.isTempSlider && (
                              <>
                                <div
                                  style={{
                                    fontSize: "1.5rem",
                                    textAlign: "center",
                                    marginTop: 10,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {checkTempSlider[item._id]
                                    ? checkTempSlider[item._id].toFixed(1)
                                    : 0}
                                  °C
                                </div>
                                <Slider
                                  className="temp-slider-width"
                                  min={-30}
                                  max={120}
                                  step={0.1}
                                  styles={{
                                    track: {
                                      background: "transparent",
                                    },
                                    tracks: {
                                      background: `rgb(109, 179, 63)`,
                                    },
                                  }}
                                  onChange={(temp) =>
                                    setCheckTempSlider((e) => ({
                                      ...e,
                                      [item._id]: temp,
                                    }))
                                  }
                                  value={
                                    checkTempSlider[item._id]
                                      ? checkTempSlider[item._id]
                                      : 0
                                  }
                                />
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Row className="opencheck-list-page-list-item">
                        <Col span={10} />
                        <Col span={2} className="opencheck-list-page-complete">
                          {item.submissionData?.isCompleted ? (
                            <CheckOutlined
                              className="checklist-complete"
                              onClick={() => {
                                handleCompleteChecklist({
                                  compliance_id: item._id,
                                  type,
                                  isCompleted: false,
                                  content:
                                    contents[item._id] ??
                                    (item.submissionData?.content?.[curLanguage] ||
                                      item.submissionData?.content?.default),
                                  temperature:
                                    checkTempSlider[item._id] ??
                                    item.submissionData?.temperature,
                                });
                              }}
                            />
                          ) : item.submissionData?.comment?.default ? (
                            <SwapOutlined
                              className="checklist-complete"
                              onClick={() => {
                                handleCompleteChecklist({
                                  compliance_id: item._id,
                                  type,
                                  isCompleted: false,
                                  comment: "",
                                  content:
                                    contents[item._id] ??
                                    (item.submissionData?.content?.[curLanguage] ||
                                      item.submissionData?.content?.default),
                                  temperature:
                                    checkTempSlider[item._id] ??
                                    item.submissionData?.temperature,
                                });
                              }}
                            />
                          ) : (
                            <CheckOutlined
                              className="checklist-pending"
                              onClick={() => {
                                if (item.isInput) {
                                  const userContent = contents[item._id]?.trim();
                                  const existingContent = (
                                    (item.submissionData?.content?.[curLanguage] ||
                                      item.submissionData?.content?.default) ??
                                    ""
                                  ).trim();
                                  if (!userContent && !existingContent) {
                                    toast.error(
                                      "Please enter content before completing."
                                    );
                                    return;
                                  }
                                }
                                handleCompleteChecklist({
                                  compliance_id: item._id,
                                  type,
                                  isCompleted: true,
                                  content:
                                    contents[item._id] ??
                                    (item.submissionData?.content?.[curLanguage] ||
                                      item.submissionData?.content?.default),
                                  temperature:
                                    checkTempSlider[item._id] ??
                                    item.submissionData?.temperature,
                                });
                              }}
                            />
                          )}
                        </Col>
                        <Col span={2} className="opencheck-list-page-tools">
                          <Popover
                            placement="right"
                            content={
                              <div className="opencheck-list-page-tools-popover">
                                {!item.submissionData?.isCompleted &&
                                  !item.submissionData?.comment?.default && (
                                    <Button
                                      variant="solid"
                                      color="pink"
                                      size="small"
                                      icon={<FormOutlined />}
                                      onClick={() => {
                                        setIsModalOpen(true);
                                        setIsModalId(item._id);
                                        setOpenPopoverId(null);
                                      }}
                                    >
                                      {t("Add Note")}
                                    </Button>
                                  )}
                              </div>
                            }
                            open={openPopoverId === item._id}
                            onOpenChange={(visible) =>
                              setOpenPopoverId(visible ? item._id : null)
                            }
                            trigger="click"
                          >
                            <MoreOutlined className="opencheck-list-page-tools-icon" />
                          </Popover>
                        </Col>
                        <Col span={10} />
                      </Row>
                    </div>
                  )}
                </List.Item>
              )}
            />
            <Flex justify="center" style={{ marginTop: "10px" }}>
              <Button
                type="primary"
                loading={isUpdateLoading}
                disabled={!allCompleted || compliances.length === 0}
                onClick={() => {
                  if (allCompleted) {
                    if (!complianceLevel?.includes(type)) {
                      requestUpdateRestaurantSetting({
                        complianceLevel: [...(complianceLevel || []), type],
                        locationId, // ✅ Pass locationId
                      });
                    } else {
                      navigate("/compliance_checks");
                    }
                  } else {
                    toast.warning(t("Please complete all checklist items"));
                  }
                }}
              >
                {t("Submit")}
              </Button>
            </Flex>
            <Modal
              title="Reason"
              open={isModalOpen}
              onOk={() => {
                if (modalContent && modalContent !== "") {
                  handleCompleteChecklist({
                    compliance_id: isModalId,
                    type,
                    isCompleted: false,
                    comment: modalContent,
                  });
                  setIsModalOpen(false);
                } else {
                  navigate("/home");
                }
              }}
              onCancel={() => setIsModalOpen(false)}
            >
              <Input.TextArea
                onChange={(e) => setModalContent(e.target.value)}
                value={modalContent}
                placeholder={t("Enter reason for note...")}
                rows={3}
              />
            </Modal>
          </>
        )}
      </Card>
    </Spin>
  );
};

export default ComplianceChecklistView;